/**
 * React Query hooks for the change register (P421).
 *
 * GET  /v1/twin/changes                       the register
 * POST /v1/twin/changes                       raise a change
 * PATCH /v1/twin/changes/{id}                 edit while open
 * GET  /v1/twin/changes/{id}/preview          what assessing would do
 * POST /v1/twin/changes/{id}/assess           assess (applies consequences)
 * POST /v1/twin/changes/{id}/close            close
 * GET  /v1/twin/changes/affectable            the picker
 * POST /v1/twin/changes/from-regulatory-change/{id}
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";

import { api, type ApiError } from "shared/api/client";
import type {
  Affectable,
  AffectedKind,
  ChangeEventRow,
  ChangeIn,
  ChangePreview,
} from "shared/api/types";

export const changeKeys = {
  register: () => ["changes", "register"] as const,
  preview: (id: string) => ["changes", "preview", id] as const,
  affectable: (kind: AffectedKind, q: string) =>
    ["changes", "affectable", kind, q] as const,
};

/** Round 19 G40 -- the register filtered to a service, or to the
 *  services the reader owns. */
export interface ChangeFilter {
  ibs?: string | null;
  mine?: boolean;
}

export function changesPath(filter: ChangeFilter = {}): string {
  const q = new URLSearchParams();
  if (filter.ibs) q.set("ibs_service_id", filter.ibs);
  if (filter.mine) q.set("mine", "true");
  const qs = q.toString();
  return qs ? `/v1/twin/changes?${qs}` : "/v1/twin/changes";
}

export function useChanges(
  filter: ChangeFilter = {},
): UseQueryResult<ChangeEventRow[], ApiError> {
  const path = changesPath(filter);
  return useQuery({
    // The bare register keeps its old key; a filtered one extends it,
    // so invalidating the register refreshes both.
    queryKey:
      path === "/v1/twin/changes"
        ? changeKeys.register()
        : [...changeKeys.register(), path],
    queryFn: () => api.get<ChangeEventRow[]>(path),
  });
}

export interface WatchedKinds {
  kinds: string[];
  owns_services: boolean;
}

/** Round 19 G40 -- which kinds tell the approvers when raised. */
export function useWatchedKinds(): UseQueryResult<WatchedKinds, ApiError> {
  return useQuery({
    queryKey: ["changes", "watched-kinds"],
    queryFn: () => api.get<WatchedKinds>("/v1/twin/changes/watched-kinds"),
  });
}

export function useChangePreview(
  id: string,
  enabled: boolean,
): UseQueryResult<ChangePreview, ApiError> {
  return useQuery({
    queryKey: changeKeys.preview(id),
    queryFn: () => api.get<ChangePreview>(`/v1/twin/changes/${id}/preview`),
    enabled,
  });
}

export function useAffectable(
  kind: AffectedKind,
  q: string,
  enabled: boolean,
): UseQueryResult<Affectable[], ApiError> {
  return useQuery({
    queryKey: changeKeys.affectable(kind, q),
    queryFn: () =>
      api.get<Affectable[]>(
        `/v1/twin/changes/affectable?kind=${kind}&q=${encodeURIComponent(q)}`,
      ),
    enabled,
  });
}

function useInvalidate() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ["changes"] });
}

export function useRaiseChange() {
  const invalidate = useInvalidate();
  return useMutation<ChangeEventRow, ApiError, ChangeIn>({
    mutationFn: (body) => api.post<ChangeEventRow>("/v1/twin/changes", body),
    onSuccess: invalidate,
  });
}

export function useEditChange(id: string) {
  const invalidate = useInvalidate();
  return useMutation<ChangeEventRow, ApiError, Partial<ChangeIn>>({
    mutationFn: (body) => api.patch<ChangeEventRow>(`/v1/twin/changes/${id}`, body),
    onSuccess: invalidate,
  });
}

export function useAssessChange(id: string) {
  const invalidate = useInvalidate();
  return useMutation<ChangeEventRow, ApiError, string>({
    mutationFn: (assessment) =>
      api.post<ChangeEventRow>(`/v1/twin/changes/${id}/assess`, { assessment }),
    onSuccess: invalidate,
  });
}

export function useCloseChange(id: string) {
  const invalidate = useInvalidate();
  return useMutation<ChangeEventRow, ApiError, void>({
    mutationFn: () => api.post<ChangeEventRow>(`/v1/twin/changes/${id}/close`, {}),
    onSuccess: invalidate,
  });
}

export function useRaiseFromRegulatoryChange() {
  const invalidate = useInvalidate();
  return useMutation<ChangeEventRow, ApiError, string>({
    mutationFn: (assessmentId) =>
      api.post<ChangeEventRow>(
        `/v1/twin/changes/from-regulatory-change/${assessmentId}`,
        {},
      ),
    onSuccess: invalidate,
  });
}
